import { useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import {
  Activity,
  ArrowDownRight,
  ArrowUpRight,
  Bell,
  Briefcase,
  CalendarClock,
  FileEdit,
  Lock,
  Plus,
  RefreshCw,
  RotateCcw,
  Search,
  Users,
} from 'lucide-react'
import { Header } from '@/components/layout/Header'
import { PositionManager } from '@/components/positions'
import type { PositionActionRequest, PositionManagerAction } from '@/components/positions'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { useAuth } from '@/hooks/useAuth'
import { useLivePositions, usePositions } from '@/hooks/usePositions'
import type { LivePosition } from '@/lib/api'
import { cn } from '@/lib/utils'
import type { Position, PositionActivity } from '@/types'

type PositionTab = 'open' | 'closed'

function formatDate(value?: string | null): string {
  if (!value) return '—'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return value
  return date.toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' })
}

function formatPrice(value?: number | null): string {
  if (value === null || value === undefined) return '—'
  const digits = value < 1 ? 6 : value < 100 ? 4 : 2
  return `$${value.toLocaleString(undefined, { maximumFractionDigits: digits })}`
}

function activityLabel(activity: PositionActivity): string {
  if (activity.type === 'closed') return `Closed${activity.result ? ` as ${activity.result}` : ''}`
  if (activity.type === 'reopened') return 'Reopened'
  if (activity.type === 'notification') return 'Notification sent'
  return 'Discussion updated'
}

function ActivityHistory({ history }: { history: PositionActivity[] }) {
  if (history.length === 0) return null
  const recent = [...history].reverse().slice(0, 5)

  return (
    <details className="group rounded-lg border bg-muted/20 px-3 py-2">
      <summary className="cursor-pointer list-none text-xs font-medium text-muted-foreground">
        Activity history ({history.length})
        <span className="ml-1 inline-block transition-transform group-open:rotate-90">›</span>
      </summary>
      <div className="mt-3 space-y-3 border-l pl-3">
        {recent.map((activity, index) => (
          <div key={`${activity.created_at}-${activity.type}-${index}`} className="text-xs">
            <div className="flex flex-wrap items-center gap-2">
              <span className="font-medium text-foreground">{activityLabel(activity)}</span>
              <span className="text-muted-foreground">by {activity.actor || 'unknown'}</span>
              <span className="text-muted-foreground">{formatDate(activity.created_at)}</span>
            </div>
            {activity.message && <p className="mt-1 whitespace-pre-wrap text-muted-foreground">{activity.message}</p>}
            {(activity.users_added?.length || activity.users_removed?.length) ? (
              <p className="mt-1 text-muted-foreground">
                {activity.users_added?.length ? `Added: ${activity.users_added.join(', ')}` : ''}
                {activity.users_added?.length && activity.users_removed?.length ? ' · ' : ''}
                {activity.users_removed?.length ? `Removed: ${activity.users_removed.join(', ')}` : ''}
              </p>
            ) : null}
          </div>
        ))}
      </div>
    </details>
  )
}

interface PositionCardProps {
  position: Position
  live?: LivePosition
  canManage: boolean
  onAction: (action: PositionManagerAction, positionId: string) => void
}

function PositionCard({ position, live, canManage, onAction }: PositionCardProps) {
  const isOpen = position.status === 'open'
  const isLong = position.direction === 'long'
  const isShort = position.direction === 'short'
  const pnl = live?.pnl_pct
  const history = Array.isArray(position.update_history) ? position.update_history : []

  return (
    <Card className="overflow-hidden transition-colors hover:border-muted-foreground/30">
      <CardContent className="p-0">
        <div className="flex flex-col gap-4 p-4 lg:flex-row lg:items-start">
          <div className="flex min-w-0 flex-1 gap-3">
            <div className={cn(
              'flex h-10 w-10 shrink-0 items-center justify-center rounded-xl',
              isLong ? 'bg-emerald-500/15 text-emerald-500' : isShort ? 'bg-rose-500/15 text-rose-500' : 'bg-muted text-muted-foreground',
            )}>
              {isLong ? <ArrowUpRight className="h-5 w-5" /> : isShort ? <ArrowDownRight className="h-5 w-5" /> : <Briefcase className="h-5 w-5" />}
            </div>

            <div className="min-w-0 flex-1 space-y-3">
              <div className="flex flex-wrap items-center gap-2">
                <h2 className="text-base font-semibold tracking-tight">{position.symbol}</h2>
                <Badge variant="outline" className={cn(
                  'uppercase',
                  isLong ? 'border-emerald-500/40 text-emerald-500' : isShort ? 'border-rose-500/40 text-rose-500' : '',
                )}>
                  {position.direction || 'N/A'}
                </Badge>
                <Badge className={isOpen ? 'bg-emerald-600 hover:bg-emerald-600' : 'bg-slate-600 hover:bg-slate-600'}>
                  {isOpen ? 'Open' : 'Closed'}
                </Badge>
                {!isOpen && position.closed_result && (
                  <Badge className={position.closed_result === 'profit' ? 'bg-emerald-500/15 text-emerald-500 hover:bg-emerald-500/20' : 'bg-rose-500/15 text-rose-500 hover:bg-rose-500/20'}>
                    {position.closed_result === 'profit' ? 'Profit' : 'Loss'}
                  </Badge>
                )}
              </div>

              <p className="max-w-4xl whitespace-pre-wrap text-sm text-muted-foreground">
                {position.decision_reason || 'No decision rationale saved.'}
              </p>

              {!isOpen && position.closed_reason && (
                <div className="rounded-lg border border-border/70 bg-muted/30 p-3">
                  <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Close note</p>
                  <p className="mt-1 whitespace-pre-wrap text-sm">{position.closed_reason}</p>
                </div>
              )}

              <div className="flex flex-wrap gap-x-5 gap-y-2 text-xs text-muted-foreground">
                <span className="flex items-center gap-1.5">
                  <CalendarClock className="h-3.5 w-3.5" />
                  Opened {formatDate(position.created_at)} by {position.created_by}
                </span>
                {!isOpen && (
                  <span className="flex items-center gap-1.5">
                    <Lock className="h-3.5 w-3.5" />
                    Closed {formatDate(position.closed_at)}{position.closed_by ? ` by ${position.closed_by}` : ''}
                  </span>
                )}
                <span className="flex items-center gap-1.5">
                  <Users className="h-3.5 w-3.5" />
                  {position.users_in_position?.length || 0} participant{position.users_in_position?.length === 1 ? '' : 's'}
                </span>
              </div>

              {position.users_in_position?.length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                  {position.users_in_position.map(user => (
                    <Badge key={user} variant="secondary" className="font-normal">@{user}</Badge>
                  ))}
                </div>
              )}

              <ActivityHistory history={history} />
            </div>
          </div>

          <div className="flex shrink-0 flex-col gap-3 border-t pt-3 lg:min-w-48 lg:border-l lg:border-t-0 lg:pl-4 lg:pt-0">
            {isOpen ? (
              <div className="grid grid-cols-3 gap-3 lg:grid-cols-1">
                <div>
                  <p className="text-[11px] uppercase tracking-wide text-muted-foreground">Entry</p>
                  <p className="mt-0.5 text-sm font-medium tabular-nums">{formatPrice(live?.entry_price ?? position.entry_price)}</p>
                </div>
                <div>
                  <p className="text-[11px] uppercase tracking-wide text-muted-foreground">Current</p>
                  <p className="mt-0.5 text-sm font-medium tabular-nums">{formatPrice(live?.current_price)}</p>
                </div>
                <div>
                  <p className="text-[11px] uppercase tracking-wide text-muted-foreground">PnL</p>
                  <p className={cn('mt-0.5 text-sm font-semibold tabular-nums', pnl === null || pnl === undefined ? 'text-muted-foreground' : pnl >= 0 ? 'text-emerald-500' : 'text-rose-500')}>
                    {pnl === null || pnl === undefined ? '—' : `${pnl >= 0 ? '+' : ''}${pnl.toFixed(2)}%`}
                  </p>
                </div>
              </div>
            ) : (
              <div>
                <p className="text-[11px] uppercase tracking-wide text-muted-foreground">Entry price</p>
                <p className="mt-0.5 text-sm font-medium tabular-nums">{formatPrice(position.entry_price)}</p>
              </div>
            )}

            {canManage && (
              <div className="flex gap-2 lg:mt-auto">
                <Button size="sm" variant="outline" className="flex-1" onClick={() => onAction('update', position.id)}>
                  <FileEdit className="h-3.5 w-3.5" />
                  Update
                </Button>
                {isOpen ? (
                  <Button size="sm" variant="destructive" className="flex-1" onClick={() => onAction('close', position.id)}>
                    <Lock className="h-3.5 w-3.5" />
                    Close
                  </Button>
                ) : (
                  <Button size="sm" className="flex-1 bg-amber-600 text-white hover:bg-amber-700" onClick={() => onAction('reopen', position.id)}>
                    <RotateCcw className="h-3.5 w-3.5" />
                    Reopen
                  </Button>
                )}
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  )
}

export default function Positions() {
  const [searchParams, setSearchParams] = useSearchParams()
  const initialSymbol = (searchParams.get('symbol') || '').trim().toUpperCase()
  const initialTab: PositionTab = searchParams.get('status') === 'closed' ? 'closed' : 'open'
  const [activeTab, setActiveTab] = useState<PositionTab>(initialTab)
  const [search, setSearch] = useState(initialSymbol)
  const [actionRequest, setActionRequest] = useState<PositionActionRequest | null>(null)
  const { hasPermission } = useAuth()
  const { data, isLoading, isFetching, error, refetch } = usePositions()
  const { data: liveData } = useLivePositions()

  const positions = data?.positions || []
  const openPositions = positions.filter(position => position.status === 'open')
  const closedPositions = positions.filter(position => position.status === 'closed')
  const profitCount = closedPositions.filter(position => position.closed_result === 'profit').length
  const decidedClosedCount = closedPositions.filter(position => position.closed_result === 'profit' || position.closed_result === 'loss').length
  const winRate = decidedClosedCount > 0 ? Math.round((profitCount / decidedClosedCount) * 100) : null
  const participantCount = new Set(openPositions.flatMap(position => position.users_in_position || [])).size
  const liveById = useMemo(() => new Map((liveData?.positions || []).map(position => [position.id, position])), [liveData])
  const canManage = hasPermission('manage_positions')

  const visiblePositions = useMemo(() => {
    const source = activeTab === 'open' ? openPositions : closedPositions
    const query = search.trim().toLowerCase()
    if (!query) return source
    return source.filter(position => [
      position.symbol,
      position.created_by,
      position.decision_reason,
      position.closed_reason,
      ...(position.users_in_position || []),
    ].some(value => String(value || '').toLowerCase().includes(query)))
  }, [activeTab, closedPositions, openPositions, search])

  const requestAction = (action: PositionManagerAction, positionId?: string) => {
    setActionRequest({
      requestId: Date.now(),
      action,
      positionId,
      symbol: action === 'create' && search.trim() ? search.trim().toUpperCase() : undefined,
    })
  }

  const changeTab = (value: string) => {
    const nextTab = value as PositionTab
    setActiveTab(nextTab)
    const nextParams = new URLSearchParams(searchParams)
    nextParams.set('status', nextTab)
    setSearchParams(nextParams, { replace: true })
  }

  return (
    <div className="flex flex-col">
      <Header title="Positions" />

      <div className="flex-1 space-y-6 p-4 md:p-6">
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">Position Discussions</h1>
            <p className="mt-1 text-sm text-muted-foreground">
              Review open and closed trades, record updates, and manage each discussion directly.
            </p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => refetch()} disabled={isFetching}>
              <RefreshCw className={cn('h-4 w-4', isFetching && 'animate-spin')} />
              Refresh
            </Button>
            <Button size="sm" className="bg-teal-600 text-white hover:bg-teal-700" onClick={() => requestAction('create')}>
              <Plus className="h-4 w-4" />
              New Discussion
            </Button>
          </div>
        </div>

        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
          <Card><CardContent className="flex items-center gap-3 p-4"><Activity className="h-5 w-5 text-emerald-500" /><div><p className="text-2xl font-semibold tabular-nums">{openPositions.length}</p><p className="text-xs text-muted-foreground">Open positions</p></div></CardContent></Card>
          <Card><CardContent className="flex items-center gap-3 p-4"><Lock className="h-5 w-5 text-slate-400" /><div><p className="text-2xl font-semibold tabular-nums">{closedPositions.length}</p><p className="text-xs text-muted-foreground">Closed positions</p></div></CardContent></Card>
          <Card><CardContent className="flex items-center gap-3 p-4"><ArrowUpRight className="h-5 w-5 text-emerald-500" /><div><p className="text-2xl font-semibold tabular-nums">{winRate === null ? '—' : `${winRate}%`}</p><p className="text-xs text-muted-foreground">Closed win rate</p></div></CardContent></Card>
          <Card><CardContent className="flex items-center gap-3 p-4"><Users className="h-5 w-5 text-blue-500" /><div><p className="text-2xl font-semibold tabular-nums">{participantCount}</p><p className="text-xs text-muted-foreground">Active participants</p></div></CardContent></Card>
        </div>

        <Tabs value={activeTab} onValueChange={changeTab}>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <TabsList>
              <TabsTrigger value="open" className="gap-2">
                Open <Badge variant="secondary" className="px-1.5 py-0">{openPositions.length}</Badge>
              </TabsTrigger>
              <TabsTrigger value="closed" className="gap-2">
                Closed <Badge variant="secondary" className="px-1.5 py-0">{closedPositions.length}</Badge>
              </TabsTrigger>
            </TabsList>
            <div className="relative w-full sm:w-80">
              <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input value={search} onChange={event => setSearch(event.target.value)} placeholder="Search symbol, user, or note..." className="pl-9" />
            </div>
          </div>

          {(['open', 'closed'] as PositionTab[]).map(tab => (
            <TabsContent key={tab} value={tab} className="mt-4 space-y-3">
              {isLoading ? (
                <Card><CardContent className="flex items-center justify-center py-16 text-sm text-muted-foreground"><RefreshCw className="mr-2 h-4 w-4 animate-spin" />Loading positions...</CardContent></Card>
              ) : error ? (
                <Card className="border-destructive/50"><CardContent className="py-10 text-center text-sm text-destructive">Failed to load positions: {(error as Error).message}</CardContent></Card>
              ) : visiblePositions.length === 0 ? (
                <Card><CardContent className="flex flex-col items-center justify-center py-16 text-center"><Briefcase className="mb-3 h-8 w-8 text-muted-foreground" /><p className="font-medium">No {tab} positions found</p><p className="mt-1 text-sm text-muted-foreground">{search ? 'Try a different search.' : tab === 'open' ? 'Create a discussion when you enter a trade.' : 'Closed discussions will appear here.'}</p></CardContent></Card>
              ) : (
                visiblePositions.map(position => (
                  <PositionCard key={position.id} position={position} live={liveById.get(position.id)} canManage={canManage} onAction={requestAction} />
                ))
              )}
            </TabsContent>
          ))}
        </Tabs>

        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <Bell className="h-3.5 w-3.5" />
          Position notifications can be sent to participants for both open and closed discussions from the Notify menu.
        </div>
      </div>

      <PositionManager actionRequest={actionRequest} hideTrigger />
    </div>
  )
}
